import { ReactNode, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';

import Typography from '@mui/material/Typography';

import CosmLogo from '@/assets/cosm_logo.png';
import CXVideoHub from '@/assets/CX_VideoHub.svg';
import { Alert, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { cx } from '@/styles/muiTheme';
import AppVersion from '@/shared/components/AppVersion';
import HtmlMessage from '@/shared/components/HtmlMessage';
import TopBanner from '@/shared/components/TopBanner';
import parseBannerMessage from '../functions/parseBannerMessage';

type Props = {
  children: ReactNode;
  /**
   * Keeps `children` on screen when the `disable-login` flag is on. Only the MCP
   * consent page sets it: its users are already signed in, so hiding it would
   * break MCP clients without stopping anyone from logging in.
   */
  ignoreDisableLogin?: boolean;
};

// Backdrop shared by every auth screen (sign-in, forgot, change, reset), since
// they all render through this template.
//
// Layer order matters: in `background-image` the FIRST layer paints on top, so
// the vignette leads and darkens the glows at the edges rather than sitting
// under them. Built from the blue tokens rather than free hex so it stays tied
// to the palette.
const AUTH_BACKDROP = [
  // Vignette — transparent through the middle, so it frames the centred card
  // without dimming it.
  `radial-gradient(120% 90% at 50% 45%, transparent 40%, ${alpha('#000000', 0.55)} 100%)`,
  // Primary glow, upper left.
  `radial-gradient(60rem 60rem at 12% 8%, ${alpha(cx.blue.main, 0.18)}, transparent 60%)`,
  // Lighter counterweight, lower right — offset so the field is not symmetrical.
  `radial-gradient(45rem 45rem at 88% 92%, ${alpha(cx.blue.hover, 0.12)}, transparent 60%)`,
].join(', ');

export default ({ children, ignoreDisableLogin }: Props) => {
  // Both flags are evaluated against LaunchDarkly's anonymous context, since
  // `identifyUser` only runs once the user is through Cognito — so they have to
  // be served by the flag's default rule to ever take effect here.
  const { bannerMessage, disableLogin } = useFlags();
  const banner = useMemo(() => parseBannerMessage(bannerMessage), [bannerMessage]);
  // `disable-login` in LaunchDarkly; the SDK camel-cases keys. Only an explicit
  // `true` hides, so a mistyped flag or a blocked SDK leaves login usable.
  const hideContent = disableLogin === true && !ignoreDisableLogin;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        // Column flex so `banner` can take its natural height off the top and
        // the card centres in whatever is left, rather than being pushed down
        // past the fold.
        display: 'flex',
        flexDirection: 'column',
        // Longhands, not the `background` shorthand: the shorthand resets
        // background-image/color against each other.
        backgroundColor: cx.bg.base,
        backgroundImage: AUTH_BACKDROP,
      }}
    >
      {banner?.placement === 'top' && <TopBanner severity={banner.severity} html={banner.text} />}
      <Container maxWidth="sm" fixed sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            // Was `height: '100vh'`, which added the banner's height to a full
            // viewport and scrolled the page. Filling the leftover space instead
            // is identical when there is no banner.
            flex: 1,
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            sx={{
              width: '100%',
              borderRadius: 2,
              // Even padding now that the surface is visible; it used to be
              // lopsided (pt 2 / pb 1) because nothing was rendered behind it.
              p: 4,
              // Flat surface: the border alone separates the card from the
              // backdrop, with no drop shadow.
              backgroundColor: cx.bg.surface2,
              border: `1px solid ${cx.border.subtle}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  opacity: 0,
                  animation: 'fadeIn 1s ease-in-out 0.25s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                }}
              >
                {/* The 45px top margin this used to carry was standing in for the
                  card's padding, which did not exist while it was transparent. */}
                <img src={CosmLogo} alt="Cosm" style={{ height: 45 }} />
              </Box>
              <Box
                sx={{
                  opacity: 0,
                  animation: 'fadeIn 1s ease-in-out 1.25s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                }}
              >
                <img src={CXVideoHub} alt="CXVideo Hub" style={{ height: 10, marginTop: 15 }} />
              </Box>

              <Box
                sx={{
                  opacity: 0,
                  animation: 'fadeIn 1s ease-in-out 2.25s forwards',
                  '@keyframes fadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 3, mt: 1 }}>
                  with Auto-ISO&trade;
                </Typography>
              </Box>

              {banner?.placement === 'inline' && (
                // No bottom margin once `disable-login` has hidden what it
                // would otherwise separate it from.
                <Alert
                  severity={banner.severity}
                  sx={{ mb: hideContent ? 0 : 2, width: '100%', whiteSpace: 'pre-line' }}
                >
                  <HtmlMessage html={banner.text} />
                </Alert>
              )}

              {!hideContent && children}
            </Box>
          </Paper>
          <AppVersion sx={{ mt: 2 }} />
        </Box>
      </Container>
    </Box>
  );
};
